import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Layouts
import AppLayout from './components/layout/AppLayout';
import AuthLayout from './components/layout/AuthLayout';
import { PageLoading } from './components/common/LoadingSpinner';

/*
 * Every screen below is loaded with React.lazy so a cold page load downloads
 * only the shell plus the one screen being opened.  Loading all 129 screens
 * eagerly built a single 3.4 MB main bundle that every first load had to fetch
 * and parse before anything rendered, which is what made individual screens
 * look dead for tens of seconds on staging.
 *
 * Two Suspense boundaries carry the wait:
 *   - the one below covers the public auth and /print routes;
 *   - AppLayout wraps its own <Outlet/>, so in-app navigation keeps the sidebar
 *     and header on screen and only the content area shows PageLoading.
 * Keep new screens as lazy() consts; a plain `import` silently re-inflates the
 * main bundle.
 */

// Auth Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

// App Pages
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const UserFormPage = lazy(() => import('./pages/users/UserFormPage'));
const RoleListPage = lazy(() => import('./pages/roles/RoleListPage'));
const PermissionStudioPage = lazy(() => import('./pages/roles/PermissionStudioPage'));
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

// Phase 2 Pages
const MasterHubPage = lazy(() => import('./pages/masters/MasterHubPage'));
const MasterListPage = lazy(() => import('./pages/masters/MasterListPage'));
const WarehouseDetailPage = lazy(() => import('./pages/masters/warehouses/WarehouseDetailPage'));
const RMMasterPage = lazy(() => import('./pages/masters/RMMasterPage'));
const FGMasterPage = lazy(() => import('./pages/masters/FGMasterPage'));
// Phase 3 — FGBackFillPage removed; the bulk FG→PRC mapping flow is no
// longer needed now that /api/production/prc enforces 1-per-FG at create.
const ProcessRoutePrepPage = lazy(() => import('./pages/masters/ProcessRoutePrepPage'));
const SupplierListPage = lazy(() => import('./pages/masters/SupplierListPage'));
const SupplierForm = lazy(() => import('./pages/masters/SupplierForm'));
const SupplierDetailPage = lazy(() => import('./pages/masters/SupplierDetailPage'));
const CustomerListPage = lazy(() => import('./pages/masters/CustomerListPage'));
const CustomerForm = lazy(() => import('./pages/masters/CustomerForm'));
const CustomerDetailPage = lazy(() => import('./pages/masters/CustomerDetailPage'));
// Phase W3 — legacy WireShapeListPage removed (superseded by config-driven
// /masters/wire-shapes admin UI).
const POListPage = lazy(() => import('./pages/purchase-orders/POListPage'));
const POCreatePage = lazy(() => import('./pages/purchase-orders/POCreatePage'));
const PODetailPage = lazy(() => import('./pages/purchase-orders/PODetailPage'));
const GRNListPage = lazy(() => import('./pages/grn/GRNListPage'));
const GRNCreatePage = lazy(() => import('./pages/grn/GRNCreatePage'));
const GRNDetailPage = lazy(() => import('./pages/grn/GRNDetailPage'));
const QRLabelPage = lazy(() => import('./pages/grn/QRLabelPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const ExportsHub = lazy(() => import('./pages/exports/ExportsHub'));

// Phase 3 Pages - Sales & Marketing
const SalesHubPage = lazy(() => import('./pages/sales/SalesHubPage'));
const SalesListPage = lazy(() => import('./pages/sales/SalesListPage'));

// Phase 3 Pages - Production Management
const ProductionHubPage = lazy(() => import('./pages/production/ProductionHubPage'));
const FGInspectionPage = lazy(() => import('./pages/production/FGInspectionPage'));
const ProductionListPage = lazy(() => import('./pages/production/ProductionListPage'));
const ProductionPlanningListPage = lazy(() => import('./pages/production/ProductionPlanningListPage'));
const ProductionPlanFormPage = lazy(() => import('./pages/production/ProductionPlanFormPage'));

// Phase 4 Pages - Operations
const StoragePage = lazy(() => import('./pages/storage/StoragePage'));
const DispatchPage = lazy(() => import('./pages/dispatch/DispatchPage'));
const DispatchChallansPage = lazy(() => import('./pages/dispatch/DispatchChallansPage'));

// Production Data Entry Pages
const ProductionDataHub = lazy(() => import('./pages/production-data/ProductionDataHub'));
const DailyProductionEntryPage = lazy(() => import('./pages/production-data/DailyProductionEntryPage'));
const EquipmentRegisterPage = lazy(() => import('./pages/production-data/EquipmentRegisterPage'));
const AnnealingLogPage = lazy(() => import('./pages/production-data/AnnealingLogPage'));
const SectionProductionPage = lazy(() => import('./pages/production-data/SectionProductionPage'));
const MillTCPage = lazy(() => import('./pages/production-data/MillTCPage'));

// Lab Quality Pages
const LabQualityHub = lazy(() => import('./pages/lab-quality/LabQualityHub'));
const LabQualityModulePage = lazy(() => import('./pages/lab-quality/LabQualityModulePage'));
const NCRegisterPage = lazy(() => import('./pages/lab-quality/NCRegisterPage'));
const PrintReportPage = lazy(() => import('./pages/print/PrintReportPage'));
const PrintInquiryRegisterPage = lazy(() => import('./pages/print/PrintInquiryRegisterPage'));
const PrintPDARegisterPage = lazy(() => import('./pages/print/PrintPDARegisterPage'));
const PrintComplaintRegisterPage = lazy(() => import('./pages/print/PrintComplaintRegisterPage'));
const PrintSalesOrderLinesRegisterPage = lazy(() => import('./pages/print/PrintSalesOrderLinesRegisterPage'));
const PrintPRCPlanRegisterPage = lazy(() => import('./pages/print/PrintPRCPlanRegisterPage'));
const PrintGRNCoilLabelPage = lazy(() => import('./pages/print/PrintGRNCoilLabelPage'));
const PrintGRNCoilLabelsBulkPage = lazy(() => import('./pages/print/PrintGRNCoilLabelsBulkPage'));
const PrintAnnealingPlanRegisterPage = lazy(() => import('./pages/print/PrintAnnealingPlanRegisterPage'));
const PrintAnnealingReportPage = lazy(() => import('./pages/print/PrintAnnealingReportPage'));
const AnnealingPlanListPage = lazy(() => import('./pages/production/AnnealingPlanListPage'));
const ProductionPlanListPage = lazy(() => import('./pages/production/ProductionPlanListPage'));
const ProductionExecutionPage = lazy(() => import('./pages/production/ProductionExecutionPage'));
const NCMasterPage = lazy(() => import('./pages/masters/NCMasterPage'));
const RollerMasterPage = lazy(() => import('./pages/masters/RollerMasterPage'));
const FGInventoryPage = lazy(() => import('./pages/finished-goods/FGInventoryPage'));
const FGDetailPage = lazy(() => import('./pages/finished-goods/FGDetailPage'));
const DispatchCreatePage = lazy(() => import('./pages/dispatch/DispatchCreatePage'));
const DispatchDetailPage = lazy(() => import('./pages/dispatch/DispatchDetailPage'));
const SalesOrderListPage = lazy(() => import('./pages/sales/SalesOrderListPage'));
const SalesOrderCreatePage = lazy(() => import('./pages/sales/SalesOrderCreatePage'));
// Modules M1+M3 (Aug 2026)
const ProcessStandardMasterPage = lazy(() => import('./pages/masters/ProcessStandardMasterPage'));
const PRCSCAllotmentPage = lazy(() => import('./pages/prc/PRCSCAllotmentPage'));
const SalesOrderDetailPage = lazy(() => import('./pages/sales/SalesOrderDetailPage'));
const InquiryRegisterPage = lazy(() => import('./pages/sales/InquiryRegisterPage'));
const PDAListPage = lazy(() => import('./pages/sales/PDAListPage'));
const ComplaintsListPage = lazy(() => import('./pages/customer-care/ComplaintsListPage'));
const SupplierEvaluationListPage = lazy(() => import('./pages/operations/SupplierEvaluationListPage'));
const ReportViewPage = lazy(() => import('./pages/reports/ReportViewPage'));
const HeatTraceabilityPage = lazy(() => import('./pages/lab-quality/HeatTraceabilityPage'));
const LabQualityBulkImportPage = lazy(() => import('./pages/lab-quality/LabQualityBulkImportPage'));
const FGTestingListPage = lazy(() => import('./pages/lab-quality/FGTestingListPage'));

// RM Issue Pages
const RMIssueListPage = lazy(() => import('./pages/rm-issue/RMIssueListPage'));
const RMOutwardListPage = lazy(() => import('./pages/rm-outward/RMOutwardListPage'));
const RMOutwardCreatePage = lazy(() => import('./pages/rm-outward/RMOutwardCreatePage'));
const RMOutwardDetailPage = lazy(() => import('./pages/rm-outward/RMOutwardDetailPage'));
const StockCodeTracePage = lazy(() => import('./pages/trace/StockCodeTracePage'));
const RMIssueCreatePage = lazy(() => import('./pages/rm-issue/RMIssueCreatePage'));
const RMIssueDetailPage = lazy(() => import('./pages/rm-issue/RMIssueDetailPage'));

// Phase 3 — PRC (Process Route Card) dedicated pages
const PRCListPage = lazy(() => import('./pages/prc/PRCListPage'));
const PRCCreatePage = lazy(() => import('./pages/prc/PRCCreatePage'));
const PRCEditPage = lazy(() => import('./pages/prc/PRCEditPage'));
const PRCDetailPage = lazy(() => import('./pages/prc/PRCDetailPage'));

// Phase 4 — Annealing Plan dedicated pages
// (List page consolidated with pages/production/AnnealingPlanListPage.jsx —
//  the /annealing route now redirects there to eliminate two competing UIs
//  on the same table.)
const AnnealingCreatePage = lazy(() => import('./pages/annealing/AnnealingCreatePage'));
const AnnealingDetailPage = lazy(() => import('./pages/annealing/AnnealingDetailPage'));
const FurnaceMasterPage = lazy(() => import('./pages/masters/FurnaceMasterPage'));
// P11 — Job Work module
const JobWorkListPage = lazy(() => import('./pages/job-work/JobWorkListPage'));
const JobWorkSendPage = lazy(() => import('./pages/job-work/JobWorkSendPage'));
const SfgRegisterPage = lazy(() => import('./pages/job-work/SfgRegisterPage'));
const JobWorkPdaPage = lazy(() => import('./pages/job-work/JobWorkPdaPage'));
const JobWorkDetailPage = lazy(() => import('./pages/job-work/JobWorkDetailPage'));
// P12 — Traceability
const PRC360Page = lazy(() => import('./pages/trace/PRC360Page'));
const QrResolvePage = lazy(() => import('./pages/trace/QrResolvePage'));

// Phase 8 — Customer PO + Feasibility dedicated pages
const CustomerPOListPage = lazy(() => import('./pages/sales/customer-po/CustomerPOListPage'));
const CustomerPOCreatePage = lazy(() => import('./pages/sales/customer-po/CustomerPOCreatePage'));
const CustomerPODetailPage = lazy(() => import('./pages/sales/customer-po/CustomerPODetailPage'));
const FeasibilityListPage = lazy(() => import('./pages/sales/feasibility/FeasibilityListPage'));
const FeasibilityDetailPage = lazy(() => import('./pages/sales/feasibility/FeasibilityDetailPage'));
const PrintFeasibilityReportPage = lazy(() => import('./pages/sales/feasibility/PrintFeasibilityReportPage'));
// Phase W2 — config-driven Wire Shape Master + Shape Transformation Master
const WireShapeMasterPage = lazy(() => import('./pages/masters/wire-shapes/WireShapeMasterPage'));
const ShapeTransformationMasterPage = lazy(() => import('./pages/masters/shape-transformations/ShapeTransformationMasterPage'));
// Phase 8.7 — Dedicated Inquiry + Quotation pages
const InquiryListPage = lazy(() => import('./pages/sales/inquiries/InquiryListPage'));
const InquiryCreatePage = lazy(() => import('./pages/sales/inquiries/InquiryCreatePage'));
const InquiryDetailPage = lazy(() => import('./pages/sales/inquiries/InquiryDetailPage'));
const InquiryDashboardPage = lazy(() => import('./pages/sales/inquiries/InquiryDashboardPage'));
const InspectionListPage = lazy(() => import('./pages/inspection/InspectionListPage'));
const InspectionDetailPage = lazy(() => import('./pages/inspection/InspectionDetailPage'));
// Phase 2d — new module
const BlockedStockListPage = lazy(() => import('./pages/blocked-stock/BlockedStockListPage'));
const BlockedStockDetailPage = lazy(() => import('./pages/blocked-stock/BlockedStockDetailPage'));
const QuotationListPage = lazy(() => import('./pages/sales/quotations/QuotationListPage'));
const QuotationCreatePage = lazy(() => import('./pages/sales/quotations/QuotationCreatePage'));
const QuotationDetailPage = lazy(() => import('./pages/sales/quotations/QuotationDetailPage'));
// Batch B3 — Costing (MKT-FMT-07) dedicated pages.
const CostSheetListPage = lazy(() => import('./pages/sales/costing/CostSheetListPage'));
const CostSheetCreatePage = lazy(() => import('./pages/sales/costing/CostSheetCreatePage'));
const CostSheetDetailPage = lazy(() => import('./pages/sales/costing/CostSheetDetailPage'));
// Batch B4 — Payments (MKT-RGT-04) dedicated pages.
const PaymentCollectionListPage = lazy(() => import('./pages/sales/payments/PaymentCollectionListPage'));
const PaymentCollectionCreatePage = lazy(() => import('./pages/sales/payments/PaymentCollectionCreatePage'));
const PaymentCollectionDetailPage = lazy(() => import('./pages/sales/payments/PaymentCollectionDetailPage'));

// Role bulk assign
const RoleBulkAssignPage = lazy(() => import('./pages/roles/RoleBulkAssignPage'));
const RoleComparePage = lazy(() => import('./pages/roles/RoleComparePage'));

// Analytics
const VendorRatingPage = lazy(() => import('./pages/analytics/VendorRatingPage'));

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <ErrorBoundary>
          <Suspense fallback={<PageLoading />}>
          <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={<PublicRoute><AuthLayout><LoginPage /></AuthLayout></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><AuthLayout><ForgotPasswordPage /></AuthLayout></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><AuthLayout><ResetPasswordPage /></AuthLayout></PublicRoute>} />

            {/* Phase 2 - Lab Quality Report Designer: headless-printable route, no chrome */}
            <Route path="/print/inquiry-register" element={<PrintInquiryRegisterPage />} />
            <Route path="/print/pda-register" element={<PrintPDARegisterPage />} />
            <Route path="/print/complaint-register" element={<PrintComplaintRegisterPage />} />
            <Route path="/print/sales-order-lines-register" element={<PrintSalesOrderLinesRegisterPage />} />
            <Route path="/print/prc-plan-register" element={<PrintPRCPlanRegisterPage />} />
            <Route path="/print/grn-coil-label/:coil_id" element={<PrintGRNCoilLabelPage />} />
            <Route path="/print/grn-coil-labels-bulk/:grn_id" element={<PrintGRNCoilLabelsBulkPage />} />
            <Route path="/print/annealing-plan-register" element={<PrintAnnealingPlanRegisterPage />} />
            {/* G5.3d — Single-plan Annealing Output Report (Screenshot 1 layout) */}
            <Route path="/print/annealing/:id" element={<PrintAnnealingReportPage />} />
            {/* Phase 2e — MKT-FMT-01 Feasibility Study Report print layout */}
            <Route path="/print/feasibility/:fr_id" element={<PrintFeasibilityReportPage />} />
            <Route path="/print/:reportType/:id" element={<PrintReportPage />} />

            {/* Protected App Routes */}
            <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UserListPage />} />
              <Route path="users/new" element={<UserFormPage />} />
              <Route path="users/:userId/edit" element={<UserFormPage />} />
              <Route path="roles" element={<RoleListPage />} />
              <Route path="roles/:roleId/permissions" element={<PermissionStudioPage />} />
              <Route path="audit-logs" element={<AuditLogPage />} />
              <Route path="settings" element={<SettingsPage />} />

              {/* Phase 2 Routes */}
              <Route path="masters" element={<MasterHubPage />} />
              <Route path="masters/rm_master" element={<RMMasterPage />} />
              <Route path="masters/fg" element={<FGMasterPage />} />
              <Route path="masters/fg_master" element={<FGMasterPage />} />
              <Route path="fg-master" element={<FGMasterPage />} />
              {/* Phase 3 — Bulk FG → PRC back-fill route removed; page deleted. */}
              <Route path="masters/process-route" element={<ProcessRoutePrepPage />} />
              <Route path="masters/process-route/:prc_id" element={<ProcessRoutePrepPage />} />
              {/* Modules M1+M3 (Aug 2026) — Process Standard Master */}
              <Route path="masters/process-standard" element={<ProcessStandardMasterPage />} />
              {/* Phase 1 — dedicated master pages */}
              <Route path="masters/suppliers" element={<SupplierListPage />} />
              <Route path="masters/suppliers/new" element={<SupplierForm />} />
              <Route path="masters/suppliers/:supplier_id" element={<SupplierDetailPage />} />
              <Route path="masters/suppliers/:supplier_id/edit" element={<SupplierForm />} />
              <Route path="masters/customers" element={<CustomerListPage />} />
              <Route path="masters/customers/new" element={<CustomerForm />} />
              <Route path="masters/customers/:customer_id" element={<CustomerDetailPage />} />
              <Route path="masters/customers/:customer_id/edit" element={<CustomerForm />} />
              {/* Phase 4 — dedicated NC + Roller master pages */}
              <Route path="masters/nc-master" element={<NCMasterPage />} />
              <Route path="masters/roller-master" element={<RollerMasterPage />} />
              {/* Phase W2 — config-driven wire shape master */}
              <Route path="masters/wire-shapes" element={<WireShapeMasterPage />} />
              <Route path="masters/shape-transformations" element={<ShapeTransformationMasterPage />} />
              {/* Phase W3 — legacy /masters/wire_shapes route removed. The
                  config-driven master lives at /masters/wire-shapes (kebab). */}
              {/* Phase 2c — Warehouse detail page with Bays sub-grid.
                  MUST come BEFORE the generic /masters/:masterType route. */}
              <Route path="masters/warehouses/:id" element={<WarehouseDetailPage />} />
              <Route path="masters/:masterType" element={<MasterListPage />} />
              <Route path="purchase-orders" element={<POListPage />} />
              <Route path="purchase-orders/new" element={<POCreatePage />} />
              <Route path="purchase-orders/:poId/edit" element={<POCreatePage />} />
              <Route path="purchase-orders/:poId" element={<PODetailPage />} />
              <Route path="grn" element={<GRNListPage />} />
              <Route path="grn/new" element={<GRNCreatePage />} />
              <Route path="grn/:grnId" element={<GRNDetailPage />} />
              <Route path="grn/:grnId/labels" element={<QRLabelPage />} />
              
              {/* Reports */}
              <Route path="reports" element={<ReportsPage />} />
              <Route path="exports" element={<ExportsHub />} />

              {/* Sales & Marketing */}
              <Route path="sales" element={<SalesHubPage />} />
              <Route path="sales/inquiries/register" element={<InquiryRegisterPage />} />
              <Route path="sales/pre-dispatch" element={<PDAListPage />} />
              <Route path="sales/pda/:id" element={<ReportViewPage reportType="pre_dispatch_advice" />} />
              {/* Phase 3.3 — dedicated Sales Order pages (must win over :moduleType catch-all) */}
              <Route path="sales/sales-orders" element={<SalesOrderListPage />} />
              <Route path="sales/sales-orders/new" element={<SalesOrderCreatePage />} />
              <Route path="sales/sales-orders/:so_id/edit" element={<SalesOrderCreatePage />} />
              <Route path="sales/sales-orders/:so_id" element={<SalesOrderDetailPage />} />
              {/* Phase 8 — Customer PO + Feasibility dedicated pages (must win over :moduleType) */}
              <Route path="sales/customer-po" element={<CustomerPOListPage />} />
              <Route path="sales/customer-po/new" element={<CustomerPOCreatePage />} />
              <Route path="sales/customer-po/:cpo_id/edit" element={<CustomerPOCreatePage />} />
              <Route path="sales/customer-po/:cpo_id" element={<CustomerPODetailPage />} />
              {/* Batch B3 — Costing (MKT-FMT-07) dedicated pages. Aliases
                  `/sales/cost-sheets` for backwards-compat with the old
                  generic SalesListPage tile. */}
              <Route path="sales/costing" element={<CostSheetListPage />} />
              <Route path="sales/costing/new" element={<CostSheetCreatePage />} />
              <Route path="sales/costing/:id/edit" element={<CostSheetCreatePage />} />
              <Route path="sales/costing/:id" element={<CostSheetDetailPage />} />
              <Route path="sales/cost-sheets" element={<CostSheetListPage />} />
              <Route path="sales/cost-sheets/new" element={<CostSheetCreatePage />} />
              <Route path="sales/cost-sheets/:id" element={<CostSheetDetailPage />} />
              {/* Batch B4 — Payments (MKT-RGT-04) dedicated pages. Registered
                  BEFORE the generic sales/:moduleType fallback so
                  /sales/payments hits the rich page instead of the stub. */}
              <Route path="sales/payments" element={<PaymentCollectionListPage />} />
              <Route path="sales/payments/new" element={<PaymentCollectionCreatePage />} />
              <Route path="sales/payments/:id" element={<PaymentCollectionDetailPage />} />
              <Route path="sales/feasibility" element={<FeasibilityListPage />} />
              <Route path="sales/feasibility/:fr_id" element={<FeasibilityDetailPage />} />
              {/* Phase 8.7 — Dedicated Inquiry + Quotation pages */}
              <Route path="sales/inquiries" element={<InquiryListPage />} />
              <Route path="sales/inquiries/dashboard" element={<InquiryDashboardPage />} />
              <Route path="inspection" element={<InspectionListPage />} />
              {/* Phase 2d — new blocked-stock module */}
              <Route path="blocked-stock" element={<BlockedStockListPage />} />
              <Route path="blocked-stock/:id" element={<BlockedStockDetailPage />} />
              <Route path="inspection/:id" element={<InspectionDetailPage />} />
              <Route path="sales/inquiries/new" element={<InquiryCreatePage />} />
              {/* Bug fix (2026-07-13): the Edit button on the Inquiry Detail page
                  navigated to /sales/inquiries/:id/edit but no such route existed,
                  causing the catch-all to redirect users to the Dashboard. */}
              <Route path="sales/inquiries/:id/edit" element={<InquiryCreatePage />} />
              <Route path="sales/inquiries/:id" element={<InquiryDetailPage />} />
              <Route path="sales/quotations" element={<QuotationListPage />} />
              <Route path="sales/quotations/new" element={<QuotationCreatePage />} />
              <Route path="sales/quotations/:id/edit" element={<QuotationCreatePage />} />
              <Route path="sales/quotations/:id" element={<QuotationDetailPage />} />
              <Route path="operations/supplier-evaluation" element={<SupplierEvaluationListPage />} />
              {/* One complaint editor: the customer-care page with the 6-stage lifecycle. */}
              <Route path="sales/complaints" element={<Navigate to="/customer-care/complaints" replace />} />
              <Route path="sales/:moduleType" element={<SalesListPage />} />

              {/* Customer Care */}
              <Route path="customer-care/complaints" element={<ComplaintsListPage />} />
              <Route path="customer-care/complaints/:id" element={<ReportViewPage reportType="customer_complaint" />} />

              {/* Production Management */}
              <Route path="production" element={<ProductionHubPage />} />
              <Route path="production/annealing-plans" element={<AnnealingPlanListPage />} />
              <Route path="production/annealing-plans/:id" element={<ReportViewPage reportType="annealing_plan" />} />
              <Route path="production/annealing-schedules" element={<Navigate to="/production/annealing-plans" replace />} />
              <Route path="production/annealing-schedules/:id" element={<Navigate to="/production/annealing-plans" replace />} />
              {/* Phase 3.2 — dedicated Production Plan list (must precede :moduleType) */}
              <Route path="production/production_plans" element={<ProductionPlanListPage />} />
              <Route path="production/production-plans" element={<ProductionPlanListPage />} />
              {/* Phase 4.1 — Production Execution grid */}
              <Route path="production/execution" element={<ProductionExecutionPage />} />
              {/* Phase 5 — FG Inventory + Dispatch Create + Job Work Create */}
              {/* Section B (Aug 2026) — top-level /finished-goods lands on inventory */}
              <Route path="finished-goods" element={<Navigate to="/finished-goods/inventory" replace />} />
              <Route path="finished-goods/inventory" element={<FGInventoryPage />} />
              <Route path="finished-goods/:id" element={<FGDetailPage />} />
              <Route path="dispatch/new" element={<DispatchCreatePage />} />
              <Route path="job-work/new" element={<JobWorkSendPage />} />
              <Route path="job-work/sfg" element={<SfgRegisterPage />} />
              <Route path="job-work/pda" element={<JobWorkPdaPage />} />
              {/* Phase 1 (Aug 2026) — Production→PRC becomes the planning-runs list.
                  New / Edit route to a dedicated planning form that NEVER creates a PRC master. */}
              <Route path="production/prc" element={<ProductionPlanningListPage />} />
              <Route path="production/prc/new" element={<ProductionPlanFormPage />} />
              <Route path="production/prc/runs/:runId/edit" element={<ProductionPlanFormPage />} />
              {/* Phase 3 — hard-redirect any lingering deep link that tried to
                  revive the retired generic Production→PRC form. Belt-and-braces
                  since /production/prc above already routes to the planning list. */}
              <Route path="production/prc-master" element={<Navigate to="/prc" replace />} />
              <Route path="production/prc-master/*" element={<Navigate to="/prc" replace />} />
              {/* FG Inspection is its own screen (queue + verdict), not the generic record list. */}
              <Route path="production/fg-inspection" element={<FGInspectionPage />} />
              <Route path="production/:moduleType" element={<ProductionListPage />} />

              {/* Operations */}
              {/* `inspection` is registered earlier as InspectionListPage — the Phase 10
                  register with the workflow status tabs.  This second registration of the
                  same path could never render (React Router takes the first match), so it
                  was a dead second definition of one screen. */}
              <Route path="storage" element={<StoragePage />} />
              <Route path="dispatch" element={<DispatchChallansPage />} />
              <Route path="dispatch/ready" element={<DispatchPage readyOnly={true} />} />
              <Route path="dispatch/:id" element={<DispatchDetailPage />} />

              {/* Production Data Entry Routes */}
              <Route path="production-data" element={<ProductionDataHub />} />
              <Route path="production-data/daily-production" element={<DailyProductionEntryPage />} />
              <Route path="production-data/dies" element={<EquipmentRegisterPage />} />
              <Route path="production-data/rollers" element={<EquipmentRegisterPage />} />
              <Route path="production-data/annealing" element={<AnnealingLogPage />} />
              <Route path="production-data/section-production" element={<SectionProductionPage />} />
              <Route path="production-data/mill-tc" element={<MillTCPage />} />

              {/* Lab Quality Routes */}
              <Route path="lab-quality" element={<LabQualityHub />} />
              <Route path="lab-quality/nc-register" element={<NCRegisterPage />} />
              {/* FG Testing register: its results are now taken at FG Inspection, which
                  writes this register too.  The page stays for the older rows. */}
              <Route path="lab-quality/fg-testing" element={<FGTestingListPage />} />
              <Route path="lab-quality/traceability" element={<HeatTraceabilityPage />} />
              <Route path="lab-quality/:moduleType/bulk-import" element={<LabQualityBulkImportPage />} />
              <Route path="lab-quality/rm-inspection/:id" element={<ReportViewPage reportType="rm_inspection" />} />
              <Route path="lab-quality/furnace-inspection/:id" element={<ReportViewPage reportType="furnace_inspection" />} />
              <Route path="lab-quality/mechanical-properties/:id" element={<ReportViewPage reportType="mechanical_properties" />} />
              {/* The Final Test Certificate report answers to both spellings of
                  the slug: the singular one every existing link uses, and the
                  plural register slug, so a URL built from the register path
                  resolves instead of falling through to :moduleType. */}
              <Route path="lab-quality/test-certificate/:id" element={<ReportViewPage reportType="test_certificate" />} />
              <Route path="lab-quality/test-certificates/:id" element={<ReportViewPage reportType="test_certificate" />} />
              <Route path="production/fg-inspection/:id" element={<ReportViewPage reportType="fg_inspection" />} />
              <Route path="lab-quality/:moduleType" element={<LabQualityModulePage />} />

              {/* Phase 3 — PRC (Process Route Card) dedicated pages */}
              <Route path="prc" element={<PRCListPage />} />
              <Route path="prc/new" element={<PRCCreatePage />} />
              <Route path="prc/:id/edit" element={<PRCEditPage />} />
              <Route path="prc/:prcId" element={<PRCDetailPage />} />
              {/* Modules M1+M3 (Aug 2026) — SC Allotment */}
              <Route path="prc/:prcId/sc-allotment" element={<PRCSCAllotmentPage />} />

              {/* Phase 4 — Annealing Plan dedicated pages */}
              {/* Phase G5 fix — /annealing consolidated into production route */}
              <Route path="annealing" element={<Navigate to="/production/annealing-plans" replace />} />
              <Route path="annealing/new" element={<AnnealingCreatePage />} />
              <Route path="annealing/:planId" element={<AnnealingDetailPage />} />
              <Route path="masters/furnaces" element={<FurnaceMasterPage />} />

              {/* RM Issue & Dispatch Routes */}
              <Route path="rm-issue" element={<RMIssueListPage />} />
              <Route path="rm-outward" element={<RMOutwardListPage />} />
              <Route path="rm-outward/new" element={<RMOutwardCreatePage />} />
              <Route path="rm-outward/:id/edit" element={<RMOutwardCreatePage />} />
              <Route path="rm-outward/:id" element={<RMOutwardDetailPage />} />
              <Route path="trace/stock-code" element={<StockCodeTracePage />} />
              <Route path="rm-issue/new" element={<RMIssueCreatePage />} />
              <Route path="rm-issue/:issueId" element={<RMIssueDetailPage />} />
              {/* P11 — Job Work */}
              <Route path="job-work" element={<JobWorkListPage />} />
              <Route path="job-work/:id" element={<JobWorkDetailPage />} />
              {/* P12 — End-to-end PRC Traceability */}
              <Route path="trace/prc/*" element={<PRC360Page />} />

              {/* Role Bulk Assign + Compare */}
              <Route path="roles/compare" element={<RoleComparePage />} />
              <Route path="roles/:roleId/users" element={<RoleBulkAssignPage />} />

              {/* Analytics */}
              <Route path="analytics/vendor-rating" element={<VendorRatingPage />} />

              {/* QR Trace Routes (deep link targets from QR codes) */}
              <Route path="trace/heat/:heatCode" element={<HeatTraceabilityPage />} />
              {/* Any scanned QR /trace/:etype/:id, and old coil labels. */}
              <Route path="trace/:etype/:id" element={<QrResolvePage />} />
              <Route path="grn/coils/:id" element={<QrResolvePage etype="grn_coil" />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          <Toaster richColors position="top-right" duration={2500} offset={80} />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
